import type { StoreProductResult } from "../services/ProductSearchService";

const csvEscape = (value: unknown): string => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const downloadStoreSearchReport = (
  results: StoreProductResult[],
  format: "csv" | "json",
  filenameBase: string
) => {
  if (!results.length) return;

  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `${filenameBase}-${stamp}.${format}`;

  if (format === "json") {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: "application/json" });
    triggerDownload(blob, filename);
    return;
  }

  const columns: { key: keyof StoreProductResult; label: string }[] = [
    { key: "storeId", label: "storeId" },
    { key: "storeAddress", label: "storeAddress" },
    { key: "name", label: "name" },
    { key: "brand", label: "brand" },
    { key: "sku", label: "sku" },
    { key: "barcode", label: "barcode" },
    { key: "storePrice", label: "storePrice" },
    { key: "msrp", label: "msrp" },
    { key: "inventory", label: "inventory" },
    { key: "clearance", label: "clearance" },
    { key: "backroom", label: "backroom" },
    { key: "status", label: "status" },
    { key: "distance", label: "distance" },
    { key: "error", label: "error" },
  ];
  const header = columns.map((c) => c.label).join(",");
  const rows = results.map((row) =>
    columns.map((c) => csvEscape(row[c.key])).join(",")
  );
  const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
  triggerDownload(blob, filename);
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const filterStoreSearchResults = (
  results: StoreProductResult[],
  query: string
): StoreProductResult[] => {
  const q = query.trim().toLowerCase();
  if (!q) return results;
  return results.filter((row) => {
    const haystack = [row.storeId, row.storeAddress, row.name, row.brand, row.status]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

export const isStoreResultInStock = (row: StoreProductResult) =>
  !row.error &&
  (row.status === "IN_STOCK" ||
    row.status === "in_stock" ||
    (row.inventory != null && row.inventory > 0));
